import { LonaWebComponent, html } from "../../component";
import { component } from "../../component-decorators";
import { css } from "../../component-styles";
import { DomUtils } from "../../dom";

export type Cta = {
  label: string;
  primary?: Option<boolean>;
  href?: Option<string>;
};

@component({ name: "std-landing-hero" })
export class LandingHero extends LonaWebComponent {
  private $ctaRow: HTMLElement = this.$("cta-row");
  private $visualSlot: HTMLElement = this.$("visual-slot");

  static makeWith(props: LandingHero.Props): LandingHero {
    return (LandingHero.make() as LandingHero).bind(props);
  }

  bind(props: LandingHero.Props): this {
    DomUtils.clearChildren(this);

    if (props.kicker) {
      const $kicker = document.createElement("p");
      $kicker.className = "kicker";
      $kicker.slot = "kicker";
      $kicker.textContent = props.kicker;
      this.appendChild($kicker);
    }

    {
      const $headline = document.createElement("h1");
      $headline.className = "headline";
      $headline.slot = "headline";
      $headline.textContent = props.headline;
      this.appendChild($headline);
    }

    if (props.subtitle) {
      const $subtitle = document.createElement("p");
      $subtitle.className = "subtitle";
      $subtitle.slot = "subtitle";
      $subtitle.textContent = props.subtitle;
      this.appendChild($subtitle);
    }

    DomUtils.clearChildren(this.$ctaRow);
    if (props.ctas && props.ctas.length > 0) {
      for (const cta of props.ctas) {
        this.$ctaRow.appendChild(this.$cta(cta));
      }
    }

    DomUtils.clearChildren(this.$visualSlot);
    if (props.renderVisual) {
      this.$visualSlot.appendChild(props.renderVisual());
    }

    return this;
  }

  private $cta(cta: Cta): HTMLElement {
    const $cta = document.createElement("a");
    $cta.className = "cta";
    $cta.href = cta.href ?? "#";
    $cta.textContent = cta.label;
    if (cta.primary) {
      $cta.setAttribute("primary", "");
    }
    return $cta;
  }

  static $styles = [
    css`
      :host {
        display: block;
      }

      #inner {
        gap: 0;
      }

      ::slotted(.kicker) {
        text-transform: uppercase;
        --margin-top: 0px;
        --margin-bottom: 24px;
      }

      ::slotted(.headline) {
        max-width: 920px;
        --margin-top: 0px;
        --margin-bottom: 44px;
      }

      ::slotted(.subtitle) {
        max-width: 940px;
        color: var(--text-sec);
        --margin-top: 0px;
        --margin-bottom: 48px;
      }

      #cta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 0;
        margin-bottom: 32px;
      }

      #cta-row:empty {
        display: none;
      }

      .cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 36px;
        color: var(--text-sec);
        background: var(--bg);
        border: 1px solid var(--divider);
        border-radius: 6px;
        padding: 0 14px;
        text-decoration: none;
        --font-size: var(--text-10px);
        --line-height-ratio: 1;
        font-weight: 500;
        transition: border-color 0.3s ease, box-shadow 0.3s ease;
      }

      .cta:hover {
        border-color: var(--text-ter);
        box-shadow: var(--box-shadow-light);
      }

      .cta:focus-visible {
        outline: 2px solid var(--blue);
        outline-offset: 2px;
      }

      .cta[primary] {
        color: var(--bg);
        background: var(--text-pri);
        border-color: var(--text-pri);
      }

      .cta[primary]:hover {
        box-shadow: var(--box-shadow-light);
      }

      #visual-slot {
        width: min(1120px, calc(100vw - 80px));
        position: relative;
        left: 50%;
        transform: translateX(-50%);
        margin-top: 32px;
      }

      @media (max-width: 768px) {
        #visual-slot {
          width: calc(100vw - 32px);
          margin-top: 24px;
        }

        #cta-row {
          gap: 8px;
          margin-bottom: 24px;
        }
      }

      #visual-slot:empty {
        display: none;
      }
    `,
  ];

  static $html = html`
    <std-col id="inner">
      <slot name="kicker"></slot>
      <slot name="headline"></slot>
      <slot name="subtitle"></slot>
      <div id="cta-row"></div>
      <div id="visual-slot"></div>
    </std-col>
  `;
}

export namespace LandingHero {
  export type Props = {
    kicker?: Option<string>;
    headline: string;
    subtitle?: Option<string>;
    ctas?: Option<Cta[]>;
    renderVisual?: Option<() => HTMLElement>;
  };
}
